import { useMemo } from "react";
import styled from "styled-components";
import { LinkIcon } from "@heroicons/react/24/outline";
import { Button, Summary } from "@sablier/v2-components/molecules";
import { useChainExplorer } from "@sablier/v2-hooks";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { Stream } from "@sablier/v2-models";
import { Characteristic, Ensurance } from "~/client/components/molecules";
import { useAccount, useModalTransaction } from "~/client/hooks";
import Footer from "../Footer";
import Visuals from "../Visuals";

const WrapperPartial = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    position: relative;
    flex-shrink: 0;
    align-items: center;
    width: 100%;
    height: 100%;
    padding-inline: ${(props) => props.theme.sizes.modalEdge};
    padding-top: calc(${(props) => props.theme.sizes.edge} * 3);
  }
`;

const Content = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    align-items: center;
    width: 100%;
  }
`;

const Top = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 2);
    align-items: center;
    width: 100%;
  }
`;

const Bottom = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
    align-items: center;
    width: 100%;
    margin-top: calc(${(props) => props.theme.sizes.edge} * 2);
    &:empty {
      margin: 0;
    }
  }
`;

const Extra = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 2);
    align-items: center;
    width: 100%;
    min-height: calc(${(props) => props.theme.sizes.edge} * 1);
  }
`;

const Heading = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    align-items: center;
    width: 100%;
    text-align: center;
    & > h2 {
      ${(props) => props.theme.styles.textSection}
      & {
        color: ${(props) => props.theme.colors.white};
        font-family: ${(props) => props.theme.fonts.primary};
      }
    }
  }
`;

const Description = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    align-items: center;
    width: 100%;
    max-width: 460px;
    text-align: center;

    & > p {
      ${(props) => props.theme.styles.textParagraphMaxi}
      & {
        color: ${(props) => props.theme.colors.gray100};
        line-height: 24pt;
      }
    }
  }
`;

const Details = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    row-gap: 8px;
    width: 100%;
  }
`;

const Card = styled(Summary.Card)`
  background-color: ${(props) => props.theme.colors.dark200};
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxMD} {
    padding-inline: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    ${Description} {
      width: 100%;
      max-width: 100%;
      word-break: normal;
    }
    ${Extra} {
      row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
    }
  }

  ${(props) => props.theme.medias.maxSM} {
    padding-inline: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
  }
`;

function Body() {
  const { data } = useModalTransaction();
  const { chainId } = useAccount();
  const { t } = useT();

  const explorer = useChainExplorer({
    chainId,
    hash: data.hash,
    type: "tx",
  });

  const error = useMemo(() => {
    if (_.isNil(data.error) || _.isNilOrEmptyString(data.error.message)) {
      return undefined;
    }

    const { message } = data.error;
    const short = message.slice(0, 200);
    if (message.length > 200) {
      return `${short}... ${t("structs.seeConsoleForMore")}.`;
    }

    return message;
  }, [data.error, t]);

  const heading = useMemo(() => {
    if (!_.isNilOrEmptyString(data.heading)) {
      return data.heading;
    }
    switch (data.status) {
      case "fail":
        return t("modalCards.transaction.fail");
      case "loading":
        return t("modalCards.transaction.loading");
      case "sign":
        return t("modalCards.transaction.sign");
      case "verify":
        return t("modalCards.transaction.verify");
      case "success":
        return t("modalCards.transaction.success");
      case "confirm":
      default:
        return t("modalCards.transaction.confirm");
    }
  }, [data.status, data.heading, t]);

  const prefix = useMemo(() => {
    if (!_.isNil(data.group) && data.group.streams?.length) {
      return _.capitalize(
        t(Stream.findShape(data.group?.streams[0].extension.purpose).title),
      );
    }
    return _.capitalize(t("words.stream"));
  }, [data.group, t]);

  const parts = useMemo(
    () => ({
      description: !_.isNilOrEmptyString(data.description),
      error: !_.isNilOrEmptyString(error),
      explorer: !_.isNilOrEmptyString(explorer),
      extra: !_.isNilOrEmptyString(explorer) || !_.isNilOrEmptyString(error),
      group: !_.isNil(data.group),
    }),
    [data.description, data.group, error, explorer],
  );

  return (
    <Wrapper>
      <Visuals />
      <Content>
        <Top>
          <Heading>
            <h2>{heading}</h2>
          </Heading>
          {parts.description && (
            <Description>
              <p>{data.description}</p>
            </Description>
          )}
        </Top>

        <Bottom>
          {parts.extra && (
            <Extra>
              {parts.explorer && (
                <Button
                  accent={data.status === "fail" ? "red" : "green"}
                  appearance={"transparent"}
                  title={t("structs.txOnExplorer")}
                  right={LinkIcon}
                  purpose={"external"}
                  to={explorer}
                />
              )}
              {parts.error && (
                <Ensurance
                  purpose={"fail"}
                  label={t("structs.problemEncountered")}
                  value={error || ""}
                />
              )}
            </Extra>
          )}

          {parts.group && (
            <Details>
              {data.group!.streams?.map((stream, index) => {
                return (
                  <Card
                    key={index}
                    isComplete={"hidden"}
                    purpose={"group"}
                    title={`${prefix} #${index + 1}`}
                    total={{
                      isExpected: true,
                      token: data.group?.token,
                      value: stream.amount,
                    }}
                  >
                    <Characteristic.Summary
                      cancelability={data.group!.cancelability}
                      timing={data.group!.timing}
                      stream={stream}
                    />
                  </Card>
                );
              })}
            </Details>
          )}
        </Bottom>
      </Content>
      <Footer />
    </Wrapper>
  );
}

export default Body;
